import type { AxiosRequestConfig } from 'axios'

import { Airlines } from './airlines'
import { ArtificialEtfs } from './artificialetfs'
import { Banks } from './banks'
import { Brf } from './brf'
import { CapitalMarkets } from './capitalmarkets'
import { CarRental } from './carrental'
import { Consumer } from './consumer'
import { Crypto } from './crypto'
import { Cvm } from './cvm'
import { DeputiesVotes } from './deputiesvotes'
import { Distribution } from './distribution'
import { Ecommerce } from './ecommerce'
import { Education } from './education'
import { EnergyShortage } from './energyshortage'
import { Exchange } from './exchange'
import { FinancialFraud } from './financialfraud'
import { FinancialStatement } from './financialstatement'
import { Flow } from './flow'
import { GlobalScenario } from './globalscenario'
import { Healthcare } from './healthcare'
import { Homebuilders } from './homebuilders'
import { Industrials } from './industrials'
import { InsiderTrading } from './insidertrading'
import { Insurance } from './insurance'
import { MacroReport } from './macroreport'
import { MarketStructure } from './marketstructure'
import { MergeAndAcquisitions } from './mergeandacquisitions'
import { MetalsAndMining } from './metalsandmining'
import { NlpModels } from './nlpmodels'
import { Oil } from './oil'
import { Options } from './options'
import { Payments } from './payments'
import { PerformanceAttribution } from './performanceattribution'
import { Portfolio } from './portfolio'
import { PortfolioAnalysis } from './portfolioanalysis'
import { Positions } from './positions'
import { Power } from './power'
import { PulpAndPaper } from './pulpandpaper'
import { Rail } from './rail'
import { Retail } from './retail'
import { Saas } from './saas'
import { Satellite } from './satellite'
import { Screening } from './screening'
import { ShortSqueeze } from './shortsqueeze'
import { SocialMedia } from './socialmedia'
import { StrategyPresentation } from './strategypresentation'
import { Sugar } from './sugar'
import { Surprise } from './surprise'
import { TechDisruption } from './techdisruption'
import { Test } from './test'
import { Transcripts } from './transcripts'
import { Upside } from './upside'
import { Valuation } from './valuation'
import { ValuationReport } from './valuationreport'
import { ValuationSpx } from './valuationspx'
import { Vessels } from './vessels'

export type RequestModifier = (config: AxiosRequestConfig) => AxiosRequestConfig

export interface BaseContext<
  ContextId = string,
  ChartConfig = any,
  ChartData = any
> {
  id: ContextId
  getChartId: (config: ChartConfig) => string
  getChartData: (
    config: ChartConfig,
    requestModifier?: RequestModifier
  ) => Promise<ChartData>
  getChartProps: (config: ChartConfig, data: ChartData) => any
  getChartUrl?: (config: ChartConfig) => string
}

export type ContextTypeList =
  | typeof Test.__typelist
  | typeof Airlines.Anac.__typelist
  | typeof ArtificialEtfs.MultiplePerformance.__typelist
  | typeof ArtificialEtfs.Performance.__typelist
  | typeof ArtificialEtfs.TopBottomAtts.__typelist
  | typeof Banks.Alm.__typelist
  | typeof Banks.ByBank.__typelist
  | typeof Banks.Carteiras.__typelist
  | typeof Banks.CreditoAmpliado.__typelist
  | typeof Banks.Clients.__typelist
  | typeof Banks.ClientsShare.__typelist
  | typeof Banks.ConglomeradosFinanceiros.__typelist
  | typeof Banks.ConglomeradosFinanceirosNet.__typelist
  | typeof Banks.Default.__typelist
  | typeof Banks.Fees.__typelist
  | typeof Banks.Fidcs.__typelist
  | typeof Banks.Growth.__typelist
  | typeof Banks.HoldingDiscount.__typelist
  | typeof Banks.IFData.__typelist
  | typeof Banks.ImpulsoComESemJuros.__typelist
  | typeof Banks.Leverage.__typelist
  | typeof Banks.Neobanks.__typelist
  | typeof Banks.PassivoDepositosAbsoluto.__typelist
  | typeof Banks.PassivoDepositosRelativo.__typelist
  | typeof Banks.PortfolioDefault.__typelist
  | typeof Banks.Spread.__typelist
  | typeof Banks.Rates.__typelist
  | typeof Banks.Susep.__typelist
  | typeof Banks.Valuation.__typelist
  | typeof Brf.Frango.__typelist
  | typeof Brf.Graos.__typelist
  | typeof Brf.Margens.__typelist
  | typeof Brf.Valuation.__typelist
  | typeof Brf.VolumeExportado.__typelist
  | typeof CapitalMarkets.B3Operational.__typelist
  | typeof CapitalMarkets.Distribution.__typelist
  | typeof CapitalMarkets.Valuation.__typelist
  | typeof CapitalMarkets.XpBtg.__typelist
  | typeof CarRental.AlugueisEAssinaturas.__typelist
  | typeof CarRental.Valuation.__typelist
  | typeof Consumer.AbevCosts.__typelist
  | typeof Consumer.Sgs.__typelist
  | typeof Consumer.Sidra.__typelist
  | typeof Consumer.Valuation.__typelist
  | typeof Crypto.BlockchainCom.__typelist
  | typeof Crypto.BuyBitcoinWorldWide.__typelist
  | typeof Crypto.CoinMarketCap.__typelist
  | typeof Crypto.DefiLlama.__typelist
  | typeof Crypto.Quotes.__typelist
  | typeof Crypto.TheBlockCrypto.__typelist
  | typeof Cvm.Fatores.__typelist
  | typeof Cvm.MainInfos.__typelist
  | typeof Cvm.TopBottom.__typelist
  | typeof DeputiesVotes.Partido.__typelist
  | typeof Distribution.AdicaoEChurn.__typelist
  | typeof Distribution.BandeiraBranca.__typelist
  | typeof Distribution.Demanda.__typelist
  | typeof Distribution.EtanolHidratado.__typelist
  | typeof Distribution.FuelImport.__typelist
  | typeof Distribution.Importacao.__typelist
  | typeof Distribution.Precos.__typelist
  | typeof Distribution.Share.__typelist
  | typeof Distribution.Valuation.__typelist
  | typeof Ecommerce.CategorySubsection.__typelist
  | typeof Ecommerce.Enjoei.__typelist
  | typeof Ecommerce.EnjoeiVendidosMMDelta.__typelist
  | typeof Ecommerce.Meli.__typelist
  | typeof Ecommerce.PriceBuckets.__typelist
  | typeof Ecommerce.SellersMeli.__typelist
  | typeof Ecommerce.Valuation.__typelist
  | typeof Ecommerce.Westwing.__typelist
  | typeof Education.SuperiorBar.__typelist
  | typeof Education.SuperiorSliderSeries.__typelist
  | typeof Education.Valuation.__typelist
  | typeof EnergyShortage.Overview.__typelist
  | typeof Exchange.B3Operational.__typelist
  | typeof Exchange.MarketCap.__typelist
  | typeof Exchange.PublicOffers.__typelist
  | typeof Exchange.RvRf.__typelist
  | typeof Exchange.Utvm.__typelist
  | typeof Exchange.Ufin.__typelist
  | typeof Exchange.Valuation.__typelist
  | typeof Exchange.WorldBank.__typelist
  | typeof FinancialFraud.BenfordLaw.__typelist
  | typeof FinancialStatement.NonFinancial.__typelist
  | typeof Flow.ContratosFuturos.__typelist
  | typeof Flow.KeyCharts.__typelist
  | typeof Flow.MutualFunds.__typelist
  | typeof Flow.PrevidenciaComplementar.__typelist
  | typeof GlobalScenario.BarPlot.__typelist
  | typeof Healthcare.Categorical.__typelist
  | typeof Healthcare.Series.__typelist
  | typeof Healthcare.SliderSeries.__typelist
  | typeof Healthcare.Valuation.__typelist
  | typeof Homebuilders.Abrainc.__typelist
  | typeof Homebuilders.Affordability.__typelist
  | typeof Homebuilders.CniConstructionConfidence.__typelist
  | typeof Homebuilders.Confidence.__typelist
  | typeof Homebuilders.Costs.__typelist
  | typeof Homebuilders.Fgts.__typelist
  | typeof Homebuilders.Funding.__typelist
  | typeof Homebuilders.McmvFgts.__typelist
  | typeof Homebuilders.Prices.__typelist
  | typeof Homebuilders.SecoviRmsp.__typelist
  | typeof Homebuilders.SecoviSp.__typelist
  | typeof Homebuilders.Targeting.__typelist
  | typeof Homebuilders.Valuation.__typelist
  | typeof Industrials.Valuation.__typelist
  | typeof InsiderTrading.PosicaoConsolidada.__typelist
  | typeof InsiderTrading.PosicaoConsolidadaIbov.__typelist
  | typeof InsiderTrading.PosicaoConsolidadaTopBottom.__typelist
  | typeof Insurance.Bbse.__typelist
  | typeof Insurance.Brasilcap.__typelist
  | typeof Insurance.Brasilprev.__typelist
  | typeof Insurance.Caixa.__typelist
  | typeof Insurance.Irb.__typelist
  | typeof Insurance.Ramos.__typelist
  | typeof Insurance.RouboFurto.__typelist
  | typeof Insurance.Sh1.__typelist
  | typeof MacroReport.Anfavea.__typelist
  | typeof MacroReport.Forex.__typelist
  | typeof MacroReport.Macro.__typelist
  | typeof MacroReport.Overview.__typelist
  | typeof MarketStructure.Historical.__typelist
  | typeof MarketStructure.KeyCharts.__typelist
  | typeof MarketStructure.MarketView.__typelist
  | typeof MarketStructure.SecurityView.__typelist
  | typeof MergeAndAcquisitions.HistoricalUpside.__typelist
  | typeof MetalsAndMining.Bonds.__typelist
  | typeof MetalsAndMining.BrazilImportParity.__typelist
  | typeof MetalsAndMining.ChinaCeic.__typelist
  | typeof MetalsAndMining.ChinaPmi.__typelist
  | typeof MetalsAndMining.EvEbitda12MVale.__typelist
  | typeof MetalsAndMining.EvEbitdaGerdauUsiminasMitalNucor.__typelist
  | typeof MetalsAndMining.FurnaceUtilization.__typelist
  | typeof MetalsAndMining.IoPortInventoryFreight.__typelist
  | typeof MetalsAndMining.LongSteelSpread.__typelist
  | typeof MetalsAndMining.NickelCopperPrices.__typelist
  | typeof MetalsAndMining.SteelBrazilDemand.__typelist
  | typeof MetalsAndMining.SteelChinaIoPrices.__typelist
  | typeof MetalsAndMining.SteelChinaMargin.__typelist
  | typeof MetalsAndMining.SteelChinaProdDemStoExp.__typelist
  | typeof MetalsAndMining.SteelExportPrice.__typelist
  | typeof MetalsAndMining.SteelGlobalProduction.__typelist
  | typeof MetalsAndMining.UsSteelMill.__typelist
  | typeof NlpModels.Twitter.__typelist
  | typeof NlpModels.TwitterThemeMention.__typelist
  | typeof Oil.BrentPosition.__typelist
  | typeof Oil.BrentWti.__typelist
  | typeof Oil.BrentWtiPosition.__typelist
  | typeof Oil.CrudeDemand.__typelist
  | typeof Oil.CrudeProduction.__typelist
  | typeof Oil.CrudeNglUs.__typelist
  | typeof Oil.Dispersion.__typelist
  | typeof Oil.Radar.__typelist
  | typeof Oil.RadarBrentSeries.__typelist
  | typeof Oil.Refine.__typelist
  | typeof Oil.SupplyDemand.__typelist
  | typeof Oil.UsOilRig.__typelist
  | typeof Oil.Valuation.__typelist
  | typeof Oil.WtiPosition.__typelist
  | typeof Options.Options.__typelist
  | typeof Payments.Pix.__typelist
  | typeof Payments.Valuation.__typelist
  | typeof PerformanceAttribution.Attribution.__typelist
  | typeof PerformanceAttribution.Comparison.__typelist
  | typeof PerformanceAttribution.Exposure.__typelist
  | typeof PerformanceAttribution.Funds.__typelist
  | typeof PerformanceAttribution.TopBottom.__typelist
  | typeof Portfolio.Overview.__typelist
  | typeof PortfolioAnalysis.Comparison.__typelist
  | typeof PortfolioAnalysis.Funds.__typelist
  | typeof PortfolioAnalysis.GroupedCases.__typelist
  | typeof PortfolioAnalysis.Risk.__typelist
  | typeof Positions.Options.__typelist
  | typeof Positions.Overview.__typelist
  | typeof Power.CargaHoraria.__typelist
  | typeof Power.CargaHorariaAno.__typelist
  | typeof Power.CargaHorariaDia.__typelist
  | typeof Power.DadosDiarios.__typelist
  | typeof Power.Geracao.__typelist
  | typeof Power.ImportacaoExportacao.__typelist
  | typeof Power.IntercambioCarga.__typelist
  | typeof Power.Ons.__typelist
  | typeof Power.ProducaoSubsistema.__typelist
  | typeof Power.ProducaoUsina.__typelist
  | typeof PulpAndPaper.Demanda.__typelist
  | typeof PulpAndPaper.Estoques.__typelist
  | typeof PulpAndPaper.Precos.__typelist
  | typeof PulpAndPaper.Shipments.__typelist
  | typeof PulpAndPaper.Utilizacao.__typelist
  | typeof PulpAndPaper.Valuation.__typelist
  | typeof Rail.BrazilExports.__typelist
  | typeof Rail.BrazilExportsLogisticMt.__typelist
  | typeof Rail.BrazilExportsSantosShareFromState.__typelist
  | typeof Rail.BrazilExportsStatePort.__typelist
  | typeof Rail.ChinaImports.__typelist
  | typeof Rail.Conab.__typelist
  | typeof Rail.ConabCommodityByRegion.__typelist
  | typeof Rail.ConabSafrasCorn.__typelist
  | typeof Rail.GrainsCountry.__typelist
  | typeof Rail.GrainsCountryCommodity.__typelist
  | typeof Rail.Imea.__typelist
  | typeof Rail.ImeaComercializacao.__typelist
  | typeof Rail.ImeaPrecoVenda.__typelist
  | typeof Rail.ImeaRoutes.__typelist
  | typeof Rail.LivestockCountry.__typelist
  | typeof Rail.LivestockCountryCommodity.__typelist
  | typeof Rail.UsaExports.__typelist
  | typeof Rail.WasdeGrains.__typelist
  | typeof Retail.Valuation.__typelist
  | typeof Saas.Valuation.__typelist
  | typeof Satellite.Sentinel5P.__typelist
  | typeof Screening.Backtest.__typelist
  | typeof Screening.Funds.__typelist
  | typeof ShortSqueeze.Macro.__typelist
  | typeof ShortSqueeze.HistoryScore.__typelist
  | typeof ShortSqueeze.Radar.__typelist
  | typeof ShortSqueeze.Trading.__typelist
  | typeof SocialMedia.FacebookReactionsCount.__typelist
  | typeof SocialMedia.GoogleTrends.__typelist
  | typeof SocialMedia.LinkedIn.__typelist
  | typeof SocialMedia.MinCount.__typelist
  | typeof SocialMedia.PosNeg.__typelist
  | typeof SocialMedia.PostsImpact.__typelist
  | typeof SocialMedia.ReactionsInfo.__typelist
  | typeof SocialMedia.ReclameAqui.__typelist
  | typeof SocialMedia.SimilarwebDispersion.__typelist
  | typeof SocialMedia.SimilarwebTime.__typelist
  | typeof StrategyPresentation.Earnings.__typelist
  | typeof StrategyPresentation.FluxoPosicionamento.__typelist
  | typeof StrategyPresentation.Performance.__typelist
  | typeof StrategyPresentation.Technical.__typelist
  | typeof StrategyPresentation.Valuation.__typelist
  | typeof Sugar.Etanol.__typelist
  | typeof Sugar.EtanolRatioAcucar.__typelist
  | typeof Sugar.ObservatorioDaCana.__typelist
  | typeof Surprise.Actual.__typelist
  | typeof Surprise.SellSideSentiments.__typelist
  | typeof TechDisruption.Exposures.__typelist
  | typeof TechDisruption.ComparisonExposures.__typelist
  | typeof TechDisruption.Returns.__typelist
  | typeof Transcripts.Inflation.__typelist
  | typeof Transcripts.InflationPerCountry.__typelist
  | typeof Upside.__typelist
  | typeof Valuation.Metric.__typelist
  | typeof Valuation.Ratio.__typelist
  | typeof ValuationReport.MarketCap.__typelist
  | typeof ValuationReport.Performance.__typelist
  | typeof ValuationReport.Technical.__typelist
  | typeof ValuationReport.Upside.__typelist
  | typeof ValuationReport.USEM.__typelist
  | typeof ValuationReport.Valuation.__typelist
  | typeof ValuationReport.Yield.__typelist
  | typeof ValuationSpx.Spx.__typelist
  | typeof Vessels.Exports.__typelist

export type Context = ContextTypeList['context']
export type ChartConfig = ContextTypeList['config']
export type ChartData = ContextTypeList['data']

export const getContext = (name: Context['id']): BaseContext => {
  switch (name) {
    case Test.Context.id:
      return Test.Context
    case Airlines.Anac.Context.id:
      return Airlines.Anac.Context
    case ArtificialEtfs.MultiplePerformance.Context.id:
      return ArtificialEtfs.MultiplePerformance.Context
    case ArtificialEtfs.Performance.Context.id:
      return ArtificialEtfs.Performance.Context
    case ArtificialEtfs.TopBottomAtts.Context.id:
      return ArtificialEtfs.TopBottomAtts.Context
    case Banks.Alm.Context.id:
      return Banks.Alm.Context
    case Banks.ByBank.Context.id:
      return Banks.ByBank.Context
    case Banks.Carteiras.Context.id:
      return Banks.Carteiras.Context
    case Banks.CreditoAmpliado.Context.id:
      return Banks.CreditoAmpliado.Context
    case Banks.Clients.Context.id:
      return Banks.Clients.Context
    case Banks.ClientsShare.Context.id:
      return Banks.ClientsShare.Context
    case Banks.ConglomeradosFinanceiros.Context.id:
      return Banks.ConglomeradosFinanceiros.Context
    case Banks.ConglomeradosFinanceirosNet.Context.id:
      return Banks.ConglomeradosFinanceirosNet.Context
    case Banks.Default.Context.id:
      return Banks.Default.Context
    case Banks.Fees.Context.id:
      return Banks.Fees.Context
    case Banks.Fidcs.Context.id:
      return Banks.Fidcs.Context
    case Banks.Growth.Context.id:
      return Banks.Growth.Context
    case Banks.HoldingDiscount.Context.id:
      return Banks.HoldingDiscount.Context
    case Banks.IFData.Context.id:
      return Banks.IFData.Context
    case Banks.ImpulsoComESemJuros.Context.id:
      return Banks.ImpulsoComESemJuros.Context
    case Banks.Leverage.Context.id:
      return Banks.Leverage.Context
    case Banks.Neobanks.Context.id:
      return Banks.Neobanks.Context
    case Banks.PassivoDepositosAbsoluto.Context.id:
      return Banks.PassivoDepositosAbsoluto.Context
    case Banks.PassivoDepositosRelativo.Context.id:
      return Banks.PassivoDepositosRelativo.Context
    case Banks.PortfolioDefault.Context.id:
      return Banks.PortfolioDefault.Context
    case Banks.Spread.Context.id:
      return Banks.Spread.Context
    case Banks.Rates.Context.id:
      return Banks.Rates.Context
    case Banks.Susep.Context.id:
      return Banks.Susep.Context
    case Banks.Valuation.Context.id:
      return Banks.Valuation.Context
    case Brf.Frango.Context.id:
      return Brf.Frango.Context
    case Brf.Graos.Context.id:
      return Brf.Graos.Context
    case Brf.Margens.Context.id:
      return Brf.Margens.Context
    case Brf.VolumeExportado.Context.id:
      return Brf.VolumeExportado.Context
    case Brf.Valuation.Context.id:
      return Brf.Valuation.Context
    case CapitalMarkets.B3Operational.Context.id:
      return CapitalMarkets.B3Operational.Context
    case CapitalMarkets.Distribution.Context.id:
      return CapitalMarkets.Distribution.Context
    case CapitalMarkets.Valuation.Context.id:
      return CapitalMarkets.Valuation.Context
    case CapitalMarkets.XpBtg.Context.id:
      return CapitalMarkets.XpBtg.Context
    case CarRental.AlugueisEAssinaturas.Context.id:
      return CarRental.AlugueisEAssinaturas.Context
    case CarRental.Valuation.Context.id:
      return CarRental.Valuation.Context
    case Consumer.AbevCosts.Context.id:
      return Consumer.AbevCosts.Context
    case Consumer.Sgs.Context.id:
      return Consumer.Sgs.Context
    case Consumer.Sidra.Context.id:
      return Consumer.Sidra.Context
    case Consumer.Valuation.Context.id:
      return Consumer.Valuation.Context
    case Crypto.BlockchainCom.Context.id:
      return Crypto.BlockchainCom.Context
    case Crypto.BuyBitcoinWorldWide.Context.id:
      return Crypto.BuyBitcoinWorldWide.Context
    case Crypto.CoinMarketCap.Context.id:
      return Crypto.CoinMarketCap.Context
    case Crypto.DefiLlama.Context.id:
      return Crypto.DefiLlama.Context
    case Crypto.Quotes.Context.id:
      return Crypto.Quotes.Context
    case Crypto.TheBlockCrypto.Context.id:
      return Crypto.TheBlockCrypto.Context
    case Cvm.Fatores.Context.id:
      return Cvm.Fatores.Context
    case Cvm.MainInfos.Context.id:
      return Cvm.MainInfos.Context
    case Cvm.TopBottom.Context.id:
      return Cvm.TopBottom.Context
    case DeputiesVotes.Partido.Context.id:
      return DeputiesVotes.Partido.Context
    case Distribution.AdicaoEChurn.Context.id:
      return Distribution.AdicaoEChurn.Context
    case Distribution.BandeiraBranca.Context.id:
      return Distribution.BandeiraBranca.Context
    case Distribution.Demanda.Context.id:
      return Distribution.Demanda.Context
    case Distribution.EtanolHidratado.Context.id:
      return Distribution.EtanolHidratado.Context
    case Distribution.FuelImport.Context.id:
      return Distribution.FuelImport.Context
    case Distribution.Importacao.Context.id:
      return Distribution.Importacao.Context
    case Distribution.Precos.Context.id:
      return Distribution.Precos.Context
    case Distribution.Share.Context.id:
      return Distribution.Share.Context
    case Distribution.Valuation.Context.id:
      return Distribution.Valuation.Context
    case Ecommerce.CategorySubsection.Context.id:
      return Ecommerce.CategorySubsection.Context
    case Ecommerce.Enjoei.Context.id:
      return Ecommerce.Enjoei.Context
    case Ecommerce.EnjoeiVendidosMMDelta.Context.id:
      return Ecommerce.EnjoeiVendidosMMDelta.Context
    case Ecommerce.Meli.Context.id:
      return Ecommerce.Meli.Context
    case Ecommerce.PriceBuckets.Context.id:
      return Ecommerce.PriceBuckets.Context
    case Ecommerce.SellersMeli.Context.id:
      return Ecommerce.SellersMeli.Context
    case Ecommerce.Valuation.Context.id:
      return Ecommerce.Valuation.Context
    case Ecommerce.Westwing.Context.id:
      return Ecommerce.Westwing.Context
    case Education.SuperiorBar.Context.id:
      return Education.SuperiorBar.Context
    case Education.SuperiorSliderSeries.Context.id:
      return Education.SuperiorSliderSeries.Context
    case Education.Valuation.Context.id:
      return Education.Valuation.Context
    case EnergyShortage.Overview.Context.id:
      return EnergyShortage.Overview.Context
    case Exchange.B3Operational.Context.id:
      return Exchange.B3Operational.Context
    case Exchange.MarketCap.Context.id:
      return Exchange.MarketCap.Context
    case Exchange.PublicOffers.Context.id:
      return Exchange.PublicOffers.Context
    case Exchange.RvRf.Context.id:
      return Exchange.RvRf.Context
    case Exchange.Utvm.Context.id:
      return Exchange.Utvm.Context
    case Exchange.Ufin.Context.id:
      return Exchange.Ufin.Context
    case Exchange.Valuation.Context.id:
      return Exchange.Valuation.Context
    case Exchange.WorldBank.Context.id:
      return Exchange.WorldBank.Context
    case FinancialFraud.BenfordLaw.Context.id:
      return FinancialFraud.BenfordLaw.Context
    case FinancialStatement.NonFinancial.Context.id:
      return FinancialStatement.NonFinancial.Context
    case Flow.ContratosFuturos.Context.id:
      return Flow.ContratosFuturos.Context
    case Flow.KeyCharts.Context.id:
      return Flow.KeyCharts.Context
    case Flow.MutualFunds.Context.id:
      return Flow.MutualFunds.Context
    case Flow.PrevidenciaComplementar.Context.id:
      return Flow.PrevidenciaComplementar.Context
    case GlobalScenario.BarPlot.Context.id:
      return GlobalScenario.BarPlot.Context
    case Healthcare.Categorical.Context.id:
      return Healthcare.Categorical.Context
    case Healthcare.Series.Context.id:
      return Healthcare.Series.Context
    case Healthcare.SliderSeries.Context.id:
      return Healthcare.SliderSeries.Context
    case Healthcare.Valuation.Context.id:
      return Healthcare.Valuation.Context
    case Homebuilders.Abrainc.Context.id:
      return Homebuilders.Abrainc.Context
    case Homebuilders.Affordability.Context.id:
      return Homebuilders.Affordability.Context
    case Homebuilders.CniConstructionConfidence.Context.id:
      return Homebuilders.CniConstructionConfidence.Context
    case Homebuilders.Confidence.Context.id:
      return Homebuilders.Confidence.Context
    case Homebuilders.Costs.Context.id:
      return Homebuilders.Costs.Context
    case Homebuilders.Fgts.Context.id:
      return Homebuilders.Fgts.Context
    case Homebuilders.Funding.Context.id:
      return Homebuilders.Funding.Context
    case Homebuilders.McmvFgts.Context.id:
      return Homebuilders.McmvFgts.Context
    case Homebuilders.Prices.Context.id:
      return Homebuilders.Prices.Context
    case Homebuilders.SecoviRmsp.Context.id:
      return Homebuilders.SecoviRmsp.Context
    case Homebuilders.SecoviSp.Context.id:
      return Homebuilders.SecoviSp.Context
    case Homebuilders.Targeting.Context.id:
      return Homebuilders.Targeting.Context
    case Homebuilders.Valuation.Context.id:
      return Homebuilders.Valuation.Context
    case Industrials.Valuation.Context.id:
      return Industrials.Valuation.Context
    case InsiderTrading.PosicaoConsolidada.Context.id:
      return InsiderTrading.PosicaoConsolidada.Context
    case InsiderTrading.PosicaoConsolidadaIbov.Context.id:
      return InsiderTrading.PosicaoConsolidadaIbov.Context
    case InsiderTrading.PosicaoConsolidadaTopBottom.Context.id:
      return InsiderTrading.PosicaoConsolidadaTopBottom.Context
    case Insurance.Bbse.Context.id:
      return Insurance.Bbse.Context
    case Insurance.Brasilcap.Context.id:
      return Insurance.Brasilcap.Context
    case Insurance.Brasilprev.Context.id:
      return Insurance.Brasilprev.Context
    case Insurance.Caixa.Context.id:
      return Insurance.Caixa.Context
    case Insurance.Irb.Context.id:
      return Insurance.Irb.Context
    case Insurance.Ramos.Context.id:
      return Insurance.Ramos.Context
    case Insurance.RouboFurto.Context.id:
      return Insurance.RouboFurto.Context
    case Insurance.Sh1.Context.id:
      return Insurance.Sh1.Context
    case MacroReport.Anfavea.Context.id:
      return MacroReport.Anfavea.Context
    case MacroReport.Forex.Context.id:
      return MacroReport.Forex.Context
    case MacroReport.Macro.Context.id:
      return MacroReport.Macro.Context
    case MacroReport.Overview.Context.id:
      return MacroReport.Overview.Context
    case MarketStructure.Historical.Context.id:
      return MarketStructure.Historical.Context
    case MarketStructure.KeyCharts.Context.id:
      return MarketStructure.KeyCharts.Context
    case MarketStructure.MarketView.Context.id:
      return MarketStructure.MarketView.Context
    case MarketStructure.SecurityView.Context.id:
      return MarketStructure.SecurityView.Context
    case MergeAndAcquisitions.HistoricalUpside.Context.id:
      return MergeAndAcquisitions.HistoricalUpside.Context
    case MetalsAndMining.Bonds.Context.id:
      return MetalsAndMining.Bonds.Context
    case MetalsAndMining.BrazilImportParity.Context.id:
      return MetalsAndMining.BrazilImportParity.Context
    case MetalsAndMining.ChinaCeic.Context.id:
      return MetalsAndMining.ChinaCeic.Context
    case MetalsAndMining.ChinaPmi.Context.id:
      return MetalsAndMining.ChinaPmi.Context
    case MetalsAndMining.EvEbitda12MVale.Context.id:
      return MetalsAndMining.EvEbitda12MVale.Context
    case MetalsAndMining.EvEbitdaGerdauUsiminasMitalNucor.Context.id:
      return MetalsAndMining.EvEbitdaGerdauUsiminasMitalNucor.Context
    case MetalsAndMining.FurnaceUtilization.Context.id:
      return MetalsAndMining.FurnaceUtilization.Context
    case MetalsAndMining.IoPortInventoryFreight.Context.id:
      return MetalsAndMining.IoPortInventoryFreight.Context
    case MetalsAndMining.LongSteelSpread.Context.id:
      return MetalsAndMining.LongSteelSpread.Context
    case MetalsAndMining.NickelCopperPrices.Context.id:
      return MetalsAndMining.NickelCopperPrices.Context
    case MetalsAndMining.SteelBrazilDemand.Context.id:
      return MetalsAndMining.SteelBrazilDemand.Context
    case MetalsAndMining.SteelChinaIoPrices.Context.id:
      return MetalsAndMining.SteelChinaIoPrices.Context
    case MetalsAndMining.SteelChinaMargin.Context.id:
      return MetalsAndMining.SteelChinaMargin.Context
    case MetalsAndMining.SteelChinaProdDemStoExp.Context.id:
      return MetalsAndMining.SteelChinaProdDemStoExp.Context
    case MetalsAndMining.SteelExportPrice.Context.id:
      return MetalsAndMining.SteelExportPrice.Context
    case MetalsAndMining.SteelGlobalProduction.Context.id:
      return MetalsAndMining.SteelGlobalProduction.Context
    case MetalsAndMining.UsSteelMill.Context.id:
      return MetalsAndMining.UsSteelMill.Context
    case NlpModels.Twitter.Context.id:
      return NlpModels.Twitter.Context
    case NlpModels.TwitterThemeMention.Context.id:
      return NlpModels.TwitterThemeMention.Context
    case Oil.BrentPosition.Context.id:
      return Oil.BrentPosition.Context
    case Oil.BrentWti.Context.id:
      return Oil.BrentWti.Context
    case Oil.BrentWtiPosition.Context.id:
      return Oil.BrentWtiPosition.Context
    case Oil.CrudeDemand.Context.id:
      return Oil.CrudeDemand.Context
    case Oil.CrudeNglUs.Context.id:
      return Oil.CrudeNglUs.Context
    case Oil.CrudeProduction.Context.id:
      return Oil.CrudeProduction.Context
    case Oil.Dispersion.Context.id:
      return Oil.Dispersion.Context
    case Oil.Radar.Context.id:
      return Oil.Radar.Context
    case Oil.RadarBrentSeries.Context.id:
      return Oil.RadarBrentSeries.Context
    case Oil.Refine.Context.id:
      return Oil.Refine.Context
    case Oil.SupplyDemand.Context.id:
      return Oil.SupplyDemand.Context
    case Oil.UsOilRig.Context.id:
      return Oil.UsOilRig.Context
    case Oil.Valuation.Context.id:
      return Oil.Valuation.Context
    case Oil.WtiPosition.Context.id:
      return Oil.WtiPosition.Context
    case Options.Options.Context.id:
      return Options.Options.Context
    case Payments.Pix.Context.id:
      return Payments.Pix.Context
    case Payments.Valuation.Context.id:
      return Payments.Valuation.Context
    case PerformanceAttribution.Attribution.Context.id:
      return PerformanceAttribution.Attribution.Context
    case PerformanceAttribution.Comparison.Context.id:
      return PerformanceAttribution.Comparison.Context
    case PerformanceAttribution.Exposure.Context.id:
      return PerformanceAttribution.Exposure.Context
    case PerformanceAttribution.Funds.Context.id:
      return PerformanceAttribution.Funds.Context
    case PerformanceAttribution.TopBottom.Context.id:
      return PerformanceAttribution.TopBottom.Context
    case Portfolio.Overview.Context.id:
      return Portfolio.Overview.Context
    case PortfolioAnalysis.Comparison.Context.id:
      return PortfolioAnalysis.Comparison.Context
    case PortfolioAnalysis.Funds.Context.id:
      return PortfolioAnalysis.Funds.Context
    case PortfolioAnalysis.GroupedCases.Context.id:
      return PortfolioAnalysis.GroupedCases.Context
    case PortfolioAnalysis.Risk.Context.id:
      return PortfolioAnalysis.Risk.Context
    case Positions.Options.Context.id:
      return Positions.Options.Context
    case Positions.Overview.Context.id:
      return Positions.Overview.Context
    case Power.CargaHoraria.Context.id:
      return Power.CargaHoraria.Context
    case Power.CargaHorariaAno.Context.id:
      return Power.CargaHorariaAno.Context
    case Power.CargaHorariaDia.Context.id:
      return Power.CargaHorariaDia.Context
    case Power.DadosDiarios.Context.id:
      return Power.DadosDiarios.Context
    case Power.Geracao.Context.id:
      return Power.Geracao.Context
    case Power.ImportacaoExportacao.Context.id:
      return Power.ImportacaoExportacao.Context
    case Power.IntercambioCarga.Context.id:
      return Power.IntercambioCarga.Context
    case Power.Ons.Context.id:
      return Power.Ons.Context
    case Power.ProducaoSubsistema.Context.id:
      return Power.ProducaoSubsistema.Context
    case Power.ProducaoUsina.Context.id:
      return Power.ProducaoUsina.Context
    case PulpAndPaper.Demanda.Context.id:
      return PulpAndPaper.Demanda.Context
    case PulpAndPaper.Estoques.Context.id:
      return PulpAndPaper.Estoques.Context
    case PulpAndPaper.Precos.Context.id:
      return PulpAndPaper.Precos.Context
    case PulpAndPaper.Shipments.Context.id:
      return PulpAndPaper.Shipments.Context
    case PulpAndPaper.Utilizacao.Context.id:
      return PulpAndPaper.Utilizacao.Context
    case PulpAndPaper.Valuation.Context.id:
      return PulpAndPaper.Valuation.Context
    case Rail.BrazilExports.Context.id:
      return Rail.BrazilExports.Context
    case Rail.BrazilExportsLogisticMt.Context.id:
      return Rail.BrazilExportsLogisticMt.Context
    case Rail.BrazilExportsSantosShareFromState.Context.id:
      return Rail.BrazilExportsSantosShareFromState.Context
    case Rail.BrazilExportsStatePort.Context.id:
      return Rail.BrazilExportsStatePort.Context
    case Rail.ChinaImports.Context.id:
      return Rail.ChinaImports.Context
    case Rail.Conab.Context.id:
      return Rail.Conab.Context
    case Rail.ConabCommodityByRegion.Context.id:
      return Rail.ConabCommodityByRegion.Context
    case Rail.ConabSafrasCorn.Context.id:
      return Rail.ConabSafrasCorn.Context
    case Rail.GrainsCountry.Context.id:
      return Rail.GrainsCountry.Context
    case Rail.GrainsCountryCommodity.Context.id:
      return Rail.GrainsCountryCommodity.Context
    case Rail.Imea.Context.id:
      return Rail.Imea.Context
    case Rail.ImeaComercializacao.Context.id:
      return Rail.ImeaComercializacao.Context
    case Rail.ImeaPrecoVenda.Context.id:
      return Rail.ImeaPrecoVenda.Context
    case Rail.ImeaRoutes.Context.id:
      return Rail.ImeaRoutes.Context
    case Rail.LivestockCountry.Context.id:
      return Rail.LivestockCountry.Context
    case Rail.LivestockCountryCommodity.Context.id:
      return Rail.LivestockCountryCommodity.Context
    case Rail.UsaExports.Context.id:
      return Rail.UsaExports.Context
    case Rail.WasdeGrains.Context.id:
      return Rail.WasdeGrains.Context
    case Retail.Valuation.Context.id:
      return Retail.Valuation.Context
    case Saas.Valuation.Context.id:
      return Saas.Valuation.Context
    case Satellite.Sentinel5P.Context.id:
      return Satellite.Sentinel5P.Context
    case Screening.Backtest.Context.id:
      return Screening.Backtest.Context
    case Screening.Funds.Context.id:
      return Screening.Funds.Context
    case ShortSqueeze.Macro.Context.id:
      return ShortSqueeze.Macro.Context
    case ShortSqueeze.HistoryScore.Context.id:
      return ShortSqueeze.HistoryScore.Context
    case ShortSqueeze.Radar.Context.id:
      return ShortSqueeze.Radar.Context
    case ShortSqueeze.Trading.Context.id:
      return ShortSqueeze.Trading.Context
    case SocialMedia.FacebookReactionsCount.Context.id:
      return SocialMedia.FacebookReactionsCount.Context
    case SocialMedia.GoogleTrends.Context.id:
      return SocialMedia.GoogleTrends.Context
    case SocialMedia.LinkedIn.Context.id:
      return SocialMedia.LinkedIn.Context
    case SocialMedia.MinCount.Context.id:
      return SocialMedia.MinCount.Context
    case SocialMedia.PosNeg.Context.id:
      return SocialMedia.PosNeg.Context
    case SocialMedia.PostsImpact.Context.id:
      return SocialMedia.PostsImpact.Context
    case SocialMedia.ReactionsInfo.Context.id:
      return SocialMedia.ReactionsInfo.Context
    case SocialMedia.ReclameAqui.Context.id:
      return SocialMedia.ReclameAqui.Context
    case SocialMedia.SimilarwebDispersion.Context.id:
      return SocialMedia.SimilarwebDispersion.Context
    case SocialMedia.SimilarwebTime.Context.id:
      return SocialMedia.SimilarwebTime.Context
    case StrategyPresentation.Earnings.Context.id:
      return StrategyPresentation.Earnings.Context
    case StrategyPresentation.FluxoPosicionamento.Context.id:
      return StrategyPresentation.FluxoPosicionamento.Context
    case StrategyPresentation.Performance.Context.id:
      return StrategyPresentation.Performance.Context
    case StrategyPresentation.Technical.Context.id:
      return StrategyPresentation.Technical.Context
    case StrategyPresentation.Valuation.Context.id:
      return StrategyPresentation.Valuation.Context
    case Sugar.Etanol.Context.id:
      return Sugar.Etanol.Context
    case Sugar.EtanolRatioAcucar.Context.id:
      return Sugar.EtanolRatioAcucar.Context
    case Sugar.ObservatorioDaCana.Context.id:
      return Sugar.ObservatorioDaCana.Context
    case Surprise.Actual.Context.id:
      return Surprise.Actual.Context
    case Surprise.SellSideSentiments.Context.id:
      return Surprise.SellSideSentiments.Context
    case TechDisruption.Exposures.Context.id:
      return TechDisruption.Exposures.Context
    case TechDisruption.ComparisonExposures.Context.id:
      return TechDisruption.ComparisonExposures.Context
    case TechDisruption.Returns.Context.id:
      return TechDisruption.Returns.Context
    case Transcripts.Inflation.Context.id:
      return Transcripts.Inflation.Context
    case Transcripts.InflationPerCountry.Context.id:
      return Transcripts.InflationPerCountry.Context
    case Upside.Context.id:
      return Upside.Context
    case Valuation.Metric.Context.id:
      return Valuation.Metric.Context
    case ValuationReport.Performance.Context.id:
      return ValuationReport.Performance.Context
    case ValuationReport.Technical.Context.id:
      return ValuationReport.Technical.Context
    case Valuation.Ratio.Context.id:
      return Valuation.Ratio.Context
    case ValuationReport.MarketCap.Context.id:
      return ValuationReport.MarketCap.Context
    case ValuationReport.Valuation.Context.id:
      return ValuationReport.Valuation.Context
    case ValuationReport.Yield.Context.id:
      return ValuationReport.Yield.Context
    case ValuationReport.USEM.Context.id:
      return ValuationReport.USEM.Context
    case ValuationReport.Upside.Context.id:
      return ValuationReport.Upside.Context
    case ValuationSpx.Spx.Context.id:
      return ValuationSpx.Spx.Context
    case Vessels.Exports.Context.id:
      return Vessels.Exports.Context
    default:
      throw new Error(`Unrecognized context name "${name}"`)
  }
}

export { Airlines } from './airlines'
export { ArtificialEtfs } from './artificialetfs'
export { Banks } from './banks'
export { Brf } from './brf'
export { CapitalMarkets } from './capitalmarkets'
export { CarRental } from './carrental'
export { Consumer } from './consumer'
export { Crypto } from './crypto'
export { Cvm } from './cvm'
export { DeputiesVotes } from './deputiesvotes'
export { Distribution } from './distribution'
export { Ecommerce } from './ecommerce'
export { Education } from './education'
export { EnergyShortage } from './energyshortage'
export { Exchange } from './exchange'
export { FinancialFraud } from './financialfraud'
export { FinancialStatement } from './financialstatement'
export { Flow } from './flow'
export { Healthcare } from './healthcare'
export { Homebuilders } from './homebuilders'
export { Industrials } from './industrials'
export { InsiderTrading } from './insidertrading'
export { Insurance } from './insurance'
export { MacroReport } from './macroreport'
export { MarketStructure } from './marketstructure'
export { MergeAndAcquisitions } from './mergeandacquisitions'
export { MetalsAndMining } from './metalsandmining'
export { NlpModels } from './nlpmodels'
export { Oil } from './oil'
export { Payments } from './payments'
export { PerformanceAttribution } from './performanceattribution'
export { Portfolio } from './portfolio'
export { PortfolioAnalysis } from './portfolioanalysis'
export { Positions } from './positions'
export { Power } from './power'
export { PulpAndPaper } from './pulpandpaper'
export { Rail } from './rail'
export { Retail } from './retail'
export { Saas } from './saas'
export { Screening } from './screening'
export { ShortSqueeze } from './shortsqueeze'
export { SocialMedia } from './socialmedia'
export { StrategyPresentation } from './strategypresentation'
export { Sugar } from './sugar'
export { Surprise } from './surprise'
export { TechDisruption } from './techdisruption'
export { Transcripts } from './transcripts'
export { Upside } from './upside'
export { Valuation } from './valuation'
export { ValuationReport } from './valuationreport'
export { ValuationSpx } from './valuationspx'
export { Vessels } from './vessels'
export * from './common'
